import React, { useState } from "react";
import { Row, Col, Card } from "antd";
import { useLiveSiteData, isOnDuty } from "../hooks/useLiveSiteData";
import { CANVAS_CARD_BODY_HEIGHT } from "../../FloorPlanModal/utils/constants";
import LiveSiteCanvas from "../components/LiveSiteCanvas";
import PersonnelPanel from "../components/PersonnelPanel";
import AlertHistoryPanel from "../components/AlertHistoryPanel";
import StatsCard from "../components/StatsCard";
import CanvasLegend from "../components/CanvasLegend";

export const LiveDashboardCore = ({
  machineID,
  className = "",
  onPersonnelSelect = null,
  initialPersonnelId = null,
  onViewTransformChange = null,
}) => {
  const [selectedPersonnelId, setSelectedPersonnelId] =
    useState(initialPersonnelId);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isPaused, setIsPaused] = useState(false);

  // Fetched once here and shared by the canvas and the other panels; the
  // 即時定位 widget runs the same hook (useLiveSiteData) on its own.
  const data = useLiveSiteData(machineID, { isPaused });
  const {
    personnel,
    baseStations,
    alerts,
    stats,
    connectionStatus,
    acknowledgeAlert,
    dismissAlert,
    placedSymbols,
    equipment,
  } = data;

  const handlePersonnelClick = (personnelId) => {
    setSelectedPersonnelId(personnelId);
    if (onPersonnelSelect) {
      onPersonnelSelect(personnelId);
    }
  };

  const handleAlertClick = (alert) => {
    setSelectedPersonnelId(alert.personnelId);
    if (onPersonnelSelect) {
      onPersonnelSelect(alert.personnelId);
    }
  };

  const filteredPersonnel = personnel.filter((person) => {
    const matchesSearch = person.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || person.status === statusFilter;
    return matchesSearch && matchesStatus && isOnDuty(person);
  });

  return (
    <div className={`live-dashboard-core ${className}`}>
      <Row gutter={[16, 16]} style={{ height: "100%" }}>
        <Col
          xs={24}
          sm={24}
          md={18}
          lg={18}
          xl={18}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div style={{ marginBottom: 16, flexShrink: 0 }}>
            <Row gutter={[16, 0]}>
              <Col span={18}>
                <LiveSiteCanvas
                  machineID={machineID}
                  data={data}
                  personnel={filteredPersonnel}
                  selectedPersonnelId={selectedPersonnelId}
                  onPersonnelClick={handlePersonnelClick}
                  onViewTransformChange={onViewTransformChange}
                  frame={(withStatus, body) => (
                    <Card
                      title={withStatus("即時位置追蹤")}
                      size="small"
                      bordered
                      bodyStyle={{
                        padding: 12,
                        position: "relative",
                        height: CANVAS_CARD_BODY_HEIGHT,
                      }}
                    >
                      {body}
                    </Card>
                  )}
                />
              </Col>
              <Col span={6}>
                <CanvasLegend
                  placedSymbols={placedSymbols}
                  baseStations={baseStations}
                  equipment={equipment}
                />
              </Col>
            </Row>
          </div>

          <div style={{ flex: 1, minHeight: 0 }}>
            <AlertHistoryPanel
              alerts={alerts}
              onAlertClick={handleAlertClick}
              onAcknowledge={acknowledgeAlert}
              onDismiss={dismissAlert}
            />
          </div>
        </Col>

        <Col
          xs={24}
          sm={24}
          md={6}
          lg={6}
          xl={6}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <StatsCard
            stats={stats}
            connectionStatus={connectionStatus}
            isPaused={isPaused}
            onTogglePause={() => setIsPaused(!isPaused)}
          />

          <div style={{ flex: 1, minHeight: 0, marginTop: 16 }}>
            <PersonnelPanel
              personnel={filteredPersonnel}
              selectedPersonnelId={selectedPersonnelId}
              onPersonnelClick={handlePersonnelClick}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};
